import React, { useState } from "react";
import {
  Bell,
  Check,
  AlertTriangle,
  Loader2,
  Link,
  CreditCard,
  Smartphone,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  linkDiscord,
  completeDiscordLink,
  isDiscordLinked,
} from "../../services/DiscordService";
import { useUserContext } from "../../context/UserContext";
import { resolveNotificationPreferences } from "../../services/UserService";
import type { SettingsTabProps } from "./types";
import { buildInstallUrl } from "../../services/AttributionService";
import { track } from "../../utils/analytics";

export default function NotificationsTab({
  user,
  t,
  setActiveTab,
}: SettingsTabProps) {
  const [isLinkingDiscord, setIsLinkingDiscord] = useState(false);
  const [savingKey, setSavingKey] = useState<"dailyDealsNotifications" | "crazyWootDeals" | "onlineDealNotifications" | "marketingEmails" | null>(null);
  const { updateUser } = useUserContext();
  const notificationPreferences = resolveNotificationPreferences(user);

  const updateNotificationPreference = async (
    key: "dailyDealsNotifications" | "crazyWootDeals" | "onlineDealNotifications" | "marketingEmails",
    value: boolean
  ) => {
    setSavingKey(key);
    try {
      await updateUser({
        notificationPreferences: {
          ...notificationPreferences,
          [key]: value,
        },
      } as any);
      toast.success(t("products.notificationPrefUpdated"));
    } catch (error) {
      console.error("Error updating notification preferences:", error);
      toast.error(t("products.notificationPrefFailed"));
    } finally {
      setSavingKey(null);
    }
  };

  const handleLinkDiscord = async () => {
    if (!user) return;

    setIsLinkingDiscord(true);
    try {
      const response = await linkDiscord();
      // Redirect to Discord OAuth
      window.location.href = response.redirectUrl;
    } catch (error) {
      console.error("Error linking Discord:", error);
      toast.error(t("settings.discordLinkError"));
      setIsLinkingDiscord(false);
    }
  };

  const handleCompleteDiscordLink = async (
    discordUserId: string,
    discordRefreshToken: string
  ) => {
    try {
      await completeDiscordLink({ discordUserId, discordRefreshToken });
      // Note: We would need to reload user data here, but that's handled by the parent component
      toast.success(t("settings.discordLinkedSuccess"));
    } catch (error) {
      console.error("Error completing Discord link:", error);
      toast.error(t("settings.discordLinkError"));
    }
  };

  // Handle Discord linking completion from URL parameters
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const discordLinkSuccess = urlParams.get("discord_link_success");
    const discordUserId = urlParams.get("discord_user_id");
    const discordRefreshToken = urlParams.get("discord_refresh_token");

    if (discordLinkSuccess === "true" && discordUserId && discordRefreshToken) {
      handleCompleteDiscordLink(discordUserId, discordRefreshToken);
      // Clean up URL parameters
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const discordLinked = isDiscordLinked(user);
  const hasNoRetailers = !user?.retailers || user.retailers.length === 0;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <Bell className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">
            {t("products.notificationsTitle")}
          </h3>
          <p className="text-xs text-gray-600 dark:text-dark-400">
            {t("products.notificationsDesc")}
          </p>
        </div>
      </div>

      <div className="space-y-3 border-b border-gray-200 pb-4 dark:border-dark-700">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-gray-100 dark:bg-dark-800 rounded-lg">
            <Smartphone className="h-4 w-4 text-gray-700 dark:text-dark-200" />
          </div>
          <div>
            <p className="text-xs text-gray-600 dark:text-dark-300">
              {t("products.mobileNotificationsHint")}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <a
            href={buildInstallUrl("ios")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("app_store_badge_clicked", { platform: "ios", location: "settings_notifications" })}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:border-dark-700 dark:bg-dark-800 dark:text-white dark:hover:bg-dark-700"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            App Store
          </a>
          <a
            href={buildInstallUrl("android")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("app_store_badge_clicked", { platform: "android", location: "settings_notifications" })}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 transition-colors hover:bg-gray-50 dark:border-dark-700 dark:bg-dark-800 dark:text-white dark:hover:bg-dark-700"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M3.61 2.228a1.23 1.23 0 0 0-.31.86v17.824c0 .332.12.635.32.872l10.08-9.784L3.61 2.228zm10.8 10.826 2.525-2.45 3.04 1.727c.87.495.87 1.308 0 1.803l-3.032 1.722-2.533-2.802zm5.564-1.503L6.2 3.73l9.906 10.97 3.869-2.149zm-3.872 3.254L6.196 20.27l13.78-7.822-3.873-2.643z" />
            </svg>
            Google Play
          </a>
        </div>

        <div className="divide-y divide-gray-200 dark:divide-dark-700 border-y border-gray-200 dark:border-dark-700">
          {[
            {
              key: "dailyDealsNotifications" as const,
              title: t("products.dailyDealsNotifications"),
              description: t("products.dailyDealsNotificationsDesc"),
              enabled: notificationPreferences.dailyDealsNotifications,
            },
            {
              key: "crazyWootDeals" as const,
              title: t("products.crazyWootDeals"),
              description: t("products.crazyWootDealsDesc"),
              enabled: notificationPreferences.crazyWootDeals,
            },
            {
              key: "onlineDealNotifications" as const,
              title: t("products.onlineDealNotifications"),
              description: t("products.onlineDealNotificationsDesc"),
              enabled: notificationPreferences.onlineDealNotifications,
            },
            {
              key: "marketingEmails" as const,
              title: t("products.marketingEmails"),
              description: t("products.marketingEmailsDesc"),
              enabled: notificationPreferences.marketingEmails,
            },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between gap-4 py-4">
              <div className="min-w-0">
                <h5 className="text-sm font-medium text-gray-900 dark:text-white">
                  {item.title}
                </h5>
                <p className="mt-1 text-xs text-gray-600 dark:text-dark-300">
                  {item.description}
                </p>
              </div>
              <button
                type="button"
                aria-pressed={item.enabled}
                onPointerDown={() => updateNotificationPreference(item.key, !item.enabled)}
                disabled={savingKey === item.key}
                className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors ${
                  item.enabled ? "bg-primary-600" : "bg-gray-300 dark:bg-dark-600"
                } ${savingKey === item.key ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full bg-white text-primary-600 shadow-sm transition-transform ${
                    item.enabled ? "translate-x-6" : "translate-x-1"
                  }`}
                >
                  {savingKey === item.key ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                </span>
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
            <Bell className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              {t("products.discordNotificationsTitle")}
            </h4>
            <p className="text-xs text-gray-600 dark:text-dark-400 mt-1">
              {t("products.discordNotificationsDesc")}
            </p>
          </div>
        </div>

        {hasNoRetailers ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-300">
                  {t("products.upgradeRequiredNotif")}
                </h4>
                <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
                  {t("products.discordPaidPlansOnly")}
                </p>
              </div>
            </div>
            <button
              onPointerDown={() => setActiveTab("billing")}
              className="w-full px-4 py-3 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 bg-primary-600 text-white hover:bg-primary-700 cursor-pointer"
              style={{ touchAction: "manipulation" }}
            >
              <CreditCard className="h-4 w-4" />
              {t("products.viewPlans")}
            </button>
          </div>
        ) : discordLinked ? (
          <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
            <Check className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-green-900 dark:text-green-300">
                {t("products.discordConnected")}
              </h4>
              <p className="text-xs text-green-700 dark:text-green-400 mt-1">
                {t("products.discordConnectedDesc")}
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
              <AlertTriangle className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-medium text-blue-900 dark:text-blue-300">
                  {t("products.discordNotConnected")}
                </h4>
                <p className="text-xs text-blue-700 dark:text-blue-400 mt-1">
                  {t("products.discordNotConnectedDesc")}
                </p>
              </div>
            </div>

            <button
              onPointerDown={handleLinkDiscord}
              disabled={isLinkingDiscord}
              className={`w-full px-4 py-3 rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2 ${
                isLinkingDiscord
                  ? "bg-gray-100 dark:bg-dark-700 text-gray-400 dark:text-dark-500 cursor-not-allowed"
                  : "bg-primary-600 text-white hover:bg-primary-700 cursor-pointer"
              }`}
              style={{ touchAction: "manipulation" }}
            >
              {isLinkingDiscord ? (
                <>
                  <Loader2 className="animate-spin h-4 w-4" />
                  {t("products.linkingDiscord")}
                </>
              ) : (
                <>
                  <Link className="h-4 w-4" />
                  {t("products.linkDiscordButton")}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
